import React, { useCallback, useEffect, useMemo, useState } from "react";
import { caApi } from "../../../backend_urls/caApi";

import EmployeePageHeader from "./models/employees_model/EmployeePageHeader";
import EmployeeStatsCards from "./models/employees_model/EmployeeStatsCards";
import EmployeeToolbar from "./models/employees_model/EmployeeToolbar";
import EmployeeTable from "./models/employees_model/EmployeeTable";
import EmployeeDrawer from "./models/employees_model/EmployeeDrawer";
import EmployeeCreate from "./models/employees_model/EmployeeCreate";

const EMPTY_OPTIONS = {
    users: [],
    offices: [],
    departments: [],
    teams: [],
    reporting_managers: [],
};

const EmployeePage = () => {
    const [employees, setEmployees] = useState([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [filters, setFilters] = useState({
        search: "",
        status: "",
        department: "",
        office: "",
    });

    const [selectedEmployee, setSelectedEmployee] = useState(null);

    // ========================================================
    // EMPLOYEE DRAWER
    // ========================================================
    const [updateDrawerOpen, setUpdateDrawerOpen] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [updateError, setUpdateError] = useState(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [createError, setCreateError] = useState(null);

    const [employeeOptions, setEmployeeOptions] = useState(EMPTY_OPTIONS);

    // ========================================================
    // LOAD EMPLOYEES
    // ========================================================
    const loadEmployees = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await caApi.getEmployees();
            const data = response?.data;

            const employeeList = Array.isArray(data)
                ? data
                : Array.isArray(data?.results)
                    ? data.results
                    : [];
            console.log("API employees:", employeeList);

            setEmployees(employeeList);
        } catch (err) {
            console.error("Failed to load employees:", err);

            setError(
                err?.response?.data?.detail ||
                "Unable to load employees."
            );

            setEmployees([]);
        } finally { setLoading(false); }
    }, []);
    useEffect(() => { loadEmployees(); }, [loadEmployees]);

    // ========================================================
    // FILTER OPTIONS
    // ========================================================
    const filterOptions = useMemo(() => {
        const departments = [
            ...new Set(
                employees
                    .map((employee) => employee.department_name)
                    .filter(Boolean)
            ),
        ];

        const offices = [
            ...new Set(
                employees
                    .map((employee) => employee.office_name)
                    .filter(Boolean)
            ),
        ];

        return { departments, offices };
    }, [employees]);

    // ========================================================
    // FILTERED EMPLOYEES
    // ========================================================
    const filteredEmployees = useMemo(() => {
        const search = filters.search.trim().toLowerCase();

        return employees.filter((employee) => {
            const matchesSearch =
                !search ||
                employee.user_name
                    ?.toLowerCase()
                    .includes(search) ||
                employee.user_email
                    ?.toLowerCase()
                    .includes(search) ||
                employee.employee_code
                    ?.toLowerCase()
                    .includes(search) ||
                employee.designation
                    ?.toLowerCase()
                    .includes(search);

            const matchesStatus =
                !filters.status ||
                employee.status === filters.status;

            const matchesDepartment =
                !filters.department ||
                employee.department_name ===
                    filters.department;

            const matchesOffice =
                !filters.office ||
                employee.office_name ===
                    filters.office;

            return (
                matchesSearch &&
                matchesStatus &&
                matchesDepartment &&
                matchesOffice
            );
        });
    }, [employees, filters]);

    // ========================================================
    // STATS
    // ========================================================
    const stats = useMemo(() => {
        const active = employees.filter(
            (employee) => employee.status === "active"
        ).length;

        const inactive = employees.filter(
            (employee) => employee.status === "inactive"
        ).length;

        return {
            total: employees.length,
            active,
            inactive,
        };

    }, [employees]);

    // ========================================================
    // FILTER HANDLERS
    // ========================================================
    const handleFilterChange = useCallback(
        (nextFilters) => {
            setFilters((previous) => ({
                ...previous,
                ...nextFilters,
            }));
        },
        []
    );

    const handleResetFilters = useCallback(() => {
        setFilters({
            search: "",
            status: "",
            department: "",
            office: "",
        });
    }, []);

    // ========================================================
    // EMPLOYEE DRAWER
    // ========================================================
    const handleViewEmployee = useCallback(
        (employee) => {
            setSelectedEmployee(employee);
            setUpdateError(null);
            setUpdateDrawerOpen(true);
        },
        []
    );

    const handleCloseDrawer = useCallback(() => {
        if (updateLoading) { return; }
        setUpdateDrawerOpen(false);
        setSelectedEmployee(null);
        setUpdateError(null);
    }, [updateLoading]);

    // ========================================================
    // UPDATE EMPLOYEE
    // ========================================================
    const handleUpdateEmployee = useCallback(
        async (employeeId, payload) => {
            try {
                setUpdateLoading(true);
                setUpdateError(null);

                const response = await caApi.updateEmployee(employeeId, payload);

                const updatedEmployee = response?.data;

                if (!updatedEmployee) {
                    throw new Error("Employee update returned no data.");
                }

                // Update employee in local state
                setEmployees((previous) =>
                    previous.map((employee) =>
                        employee.id === updatedEmployee.id
                            ? updatedEmployee
                            : employee
                    )
                );

                // Update currently opened drawer
                setSelectedEmployee(updatedEmployee);

                return { success: true, employee: updatedEmployee };

            } catch (err) {
                console.error("Failed to update employee:", err);

                const responseData = err?.response?.data;

                let message = responseData?.detail || responseData?.message;

                if (!message && typeof responseData === "object") {
                    message = Object.values(responseData).flat().join(" ");
                }

                const finalMessage = message || "Unable to update employee.";

                setUpdateError(finalMessage);

                return { success: false, error: finalMessage };
            } finally { setUpdateLoading(false); }
        },
        []
    );

    // ========================================================
    // CREATE EMPLOYEE
    // ========================================================
    const handleAddEmployee = useCallback(async () => {
        try {
            setCreateError(null);

            const response = await caApi.getEmployeeCreateOptions();
            setEmployeeOptions(response?.data || EMPTY_OPTIONS);

            setCreateOpen(true);
        } catch (err) {
            console.error("Failed to load employee options:", err);

            setCreateError(
                err?.response?.data?.detail ||
                "Unable to load employee creation options."
            );
        }
    }, []);

    const handleCloseCreate = useCallback(() => {
        if (createLoading) { return; }
        setCreateOpen(false);
        setCreateError(null);
    }, [createLoading]);

    const handleCreateEmployee = useCallback(
        async (payload) => {
            try {
                setCreateLoading(true);
                setCreateError(null);

                const response = await caApi.createEmployee(payload);
                const createdEmployee = response?.data;
                if (createdEmployee) {
                    setEmployees(
                        (previous) => [ createdEmployee, ...previous ]
                    );
                } else { await loadEmployees(); }
                setCreateOpen(false);
            } catch (err) {
                console.error("Failed to create employee:", err);
                const responseData = err?.response?.data;

                let message = responseData?.detail || responseData?.message;

                if (!message && typeof responseData === "object") {
                    message = Object.values(responseData).flat().join(" ");
                }

                setCreateError(message || "Unable to create employee.");
            } finally { setCreateLoading(false); }
        },
        [loadEmployees]
    );

    return (
        <div className="employee-page">
            <EmployeePageHeader onAddEmployee = { handleAddEmployee } />

            {error && (
                <div className="employee-page-error">
                    { error }
                </div>
            )}

            <EmployeeStatsCards
                stats   = { stats }
                loading = { loading }
            />

            <EmployeeToolbar
                filters       = { filters }
                filterOptions = { filterOptions }
                employeeCount = { filteredEmployees.length }
                onChange      = { handleFilterChange }
                onReset       = { handleResetFilters }
            />

            <EmployeeTable
                employees      = { filteredEmployees }
                loading        = { loading }
                onViewEmployee = { handleViewEmployee }
                onLoadData     = { loadEmployees }
            />

            <EmployeeDrawer
                isOpen   = { updateDrawerOpen }
                onClose  = { handleCloseDrawer }
                employee = { selectedEmployee }
                loading  = { updateLoading }
                error    = { updateError }
                onUpdate = { handleUpdateEmployee }
            />

            <EmployeeCreate
                isOpen   = { createOpen }
                loading  = { createLoading }
                error    = { createError }
                options  = { employeeOptions }
                onClose  = { handleCloseCreate }
                onSubmit = { handleCreateEmployee }
            />
        </div>
    );
};

export default EmployeePage;
